import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Smartphone, CheckCircle2, XCircle, Loader2, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const UPIPaymentModal = ({ isOpen, onClose, paymentData, onSuccess }) => {
  const [status, setStatus] = useState('pending'); // pending, polling, completed, failed
  const [pollCount, setPollCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [qrLoading, setQrLoading] = useState(true);
  const [finalData, setFinalData] = useState(null);
  const [showManualConfirm, setShowManualConfirm] = useState(false);
  const [manualChecking, setManualChecking] = useState(false);

  // Refs for polling control
  const pollingIntervalRef = useRef(null);
  const pollAttemptsRef = useRef(0);

  const MAX_POLL_ATTEMPTS = 60; // Poll for max 3 minutes (60 * 3 seconds)
  const POLL_INTERVAL = 3000; // 3 seconds (same as old UI)
  const SHOW_MANUAL_AFTER = 10; // Show manual option after 10 polls (30 seconds)

  // Check if mobile device
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Poll payment status - silent version for auto-polling
  const checkPaymentStatusSilent = useCallback(async () => {
    if (!paymentData?.order_id) return false;

    try {
      const response = await api.get(`/upi/status/${paymentData.order_id}`);
      const data = response.data;

      if (data.status === 'completed') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setStatus('completed');
        setFinalData(data);
        toast.success('Payment successful!');
        if (onSuccess) {
          onSuccess(data);
        }
        return true; // Stop polling
      } else if (data.status === 'failed') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setStatus('failed');
        setFinalData(data);
        toast.error(data.message || 'Payment failed');
        return true; // Stop polling
      }
      return false; // Continue polling
    } catch (error) {
      // Silently ignore errors during polling
      console.log('UPI status check error:', error);
      return false;
    }
  }, [paymentData, onSuccess]);

  // Manual status check with loading indicator
  const checkPaymentStatusManual = async () => {
    if (!paymentData?.order_id) return;

    setManualChecking(true);
    try {
      const response = await api.get(`/upi/status/${paymentData.order_id}`);
      const data = response.data;

      if (data.status === 'completed') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setStatus('completed');
        setFinalData(data);
        toast.success('Payment successful!');
        if (onSuccess) {
          onSuccess(data);
        }
      } else if (data.status === 'failed') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setStatus('failed');
        setFinalData(data);
        toast.error(data.message || 'Payment failed');
      } else {
        // Show more detailed feedback for pending status
        const hdfc_status = data.hdfc_status;
        if (hdfc_status && hdfc_status !== 'PENDING') {
          toast.info(`Bank status: ${hdfc_status}. Please wait or try again.`);
        } else {
          toast.info('Payment is still pending. Please complete in your UPI app or wait for bank confirmation.');
        }
      }
    } catch (error) {
      console.error('Status check error:', error);
      toast.error('Failed to check payment status. Please try again.');
    } finally {
      setManualChecking(false);
    }
  };

  // Start auto-polling when modal opens
  useEffect(() => {
    if (!isOpen || !paymentData?.order_id || status === 'completed' || status === 'failed') {
      return;
    }

    setStatus('polling');
    pollAttemptsRef.current = 0;
    setShowManualConfirm(false);

    // Start polling every 3 seconds
    pollingIntervalRef.current = setInterval(() => {
      pollAttemptsRef.current += 1;
      setPollCount(pollAttemptsRef.current);

      // Show manual confirmation option after SHOW_MANUAL_AFTER attempts (30 seconds)
      if (pollAttemptsRef.current >= SHOW_MANUAL_AFTER) {
        setShowManualConfirm(true);
      }

      if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
        // Stop after max attempts
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setStatus('pending');
        toast.error('Payment verification timed out. Please check your transaction history.');
        return;
      }

      checkPaymentStatusSilent();
    }, POLL_INTERVAL);

    // Initial check
    checkPaymentStatusSilent();

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isOpen, paymentData?.order_id, checkPaymentStatusSilent, status]);

  // Auto-open UPI app on mobile
  useEffect(() => {
    if (isOpen && paymentData?.upi_intent_url && isMobile && status === 'polling') {
      // Small delay to ensure modal is visible first
      const timer = setTimeout(() => {
        window.location.href = paymentData.upi_intent_url;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, paymentData?.upi_intent_url, isMobile, status]);

  // Check status when page becomes visible (user returns from UPI app)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && paymentData?.order_id && status === 'polling') {
        console.log('User returned to page, checking payment status...');
        checkPaymentStatusSilent();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [paymentData?.order_id, checkPaymentStatusSilent, status]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Stop polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setStatus('pending');
      setPollCount(0);
      setFinalData(null);
      setQrLoading(true);
      setShowManualConfirm(false);
      pollAttemptsRef.current = 0;
    }
  }, [isOpen]);

  // Handle close with cleanup
  const handleClose = () => {
    // Stop polling when closing
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setShowManualConfirm(false);
    onClose();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  const { order_id, amount, milli_amount, rate_applied, upi_intent_url, merchant_vpa, app_links } = paymentData || {};

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={status !== 'polling' ? handleClose : undefined}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              {status === 'completed' ? 'Payment Successful' : 
               status === 'failed' ? 'Payment Failed' : 
               'Complete Payment'}
            </h2>
            <button 
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Success State */}
            {status === 'completed' && (
              <div className="text-center py-6">
                <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
                <p className="text-gray-600 mb-4">
                  {finalData?.milli_amount?.toFixed(2) || milli_amount?.toFixed(2)} Milli&apos;s have been added to your account.
                </p>
                <div className="bg-green-50 rounded-xl p-4 mb-4">
                  <p className="text-sm text-green-800">
                    New Balance: <span className="font-bold">{finalData?.new_balance?.toFixed(2)} Milli&apos;s</span>
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl"
                >
                  Done
                </button>
              </div>
            )}

            {/* Failed State */}
            {status === 'failed' && (
              <div className="text-center py-6">
                <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Failed</h3>
                <p className="text-gray-600 mb-4">
                  {finalData?.message || 'Your payment could not be processed. Please try again.'}
                </p>
                <button
                  onClick={handleClose}
                  className="w-full py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-semibold rounded-xl"
                >
                  Close
                </button>
              </div>
            )}

            {/* Pending/Polling State */}
            {(status === 'pending' || status === 'polling') && (
              <>
                {/* Amount Summary */}
                <div className="bg-amber-50 rounded-xl p-4 mb-6 border border-amber-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Amount</span>
                    <span className="text-xl font-bold text-gray-900">₹{amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">You will get</span>
                    <span className="font-semibold text-amber-700">{milli_amount?.toFixed(2)} Milli&apos;s</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Rate</span>
                    <span className="text-gray-600">₹{rate_applied?.toFixed(2)}/Milli</span>
                  </div>
                </div>

                {/* QR Code Section - Hide on mobile since UPI app auto-opens */}
                {!isMobile && (
                  <div className="text-center mb-6">
                    <p className="text-sm text-gray-500 mb-3">Scan QR code with any UPI app</p>
                    <div className="relative inline-block">
                      <div className="bg-white p-3 rounded-xl border-2 border-amber-300 shadow-md">
                        {qrLoading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white rounded-xl">
                            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                          </div>
                        )}
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/upi/qr/${order_id}`}
                          alt="UPI QR Code"
                          className="w-48 h-48"
                          onLoad={() => setQrLoading(false)}
                          onError={() => setQrLoading(false)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* UPI ID */}
                <div className="bg-gray-50 rounded-xl p-3 mb-4">
                  <p className="text-xs text-gray-500 mb-1">Pay to UPI ID</p>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-medium text-gray-800">{merchant_vpa}</span>
                    <button
                      onClick={() => copyToClipboard(merchant_vpa)}
                      className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      {copied ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                {/* UPI App Links */}
                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-3 text-center">
                    {isMobile ? 'Or open manually in UPI app' : 'Or open in UPI app'}
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {upi_intent_url && (
                      <a
                        href={upi_intent_url}
                        className="flex flex-col items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-1 shadow-sm">
                          <Smartphone className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xs text-gray-600">Any UPI</span>
                      </a>
                    )}
                    {app_links?.gpay && (
                      <a
                        href={app_links.gpay}
                        className="flex flex-col items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center mb-1 shadow-sm">
                          <span className="text-lg text-white font-bold">G</span>
                        </div>
                        <span className="text-xs text-gray-600">GPay</span>
                      </a>
                    )}
                    {app_links?.phonepe && (
                      <a
                        href={app_links.phonepe}
                        className="flex flex-col items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mb-1 shadow-sm">
                          <span className="text-lg text-white font-bold">P</span>
                        </div>
                        <span className="text-xs text-gray-600">PhonePe</span>
                      </a>
                    )}
                    {app_links?.paytm && (
                      <a
                        href={app_links.paytm}
                        className="flex flex-col items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mb-1 shadow-sm">
                          <span className="text-lg text-white font-bold">₹</span>
                        </div>
                        <span className="text-xs text-gray-600">Paytm</span>
                      </a>
                    )}
                  </div>
                </div>

                {/* Waiting Indicator */}
                <div className={`rounded-xl p-4 mb-4 ${showManualConfirm ? 'bg-orange-50 border border-orange-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-3">
                    <Loader2 className={`w-5 h-5 animate-spin ${showManualConfirm ? 'text-orange-600' : 'text-amber-600'}`} />
                    <div>
                      <p className={`text-sm font-medium ${showManualConfirm ? 'text-orange-900' : 'text-amber-900'}`}>
                        {showManualConfirm ? 'Still waiting for confirmation...' : 'Auto-detecting payment...'}
                      </p>
                      <p className={`text-xs ${showManualConfirm ? 'text-orange-700' : 'text-amber-700'}`}>
                        {showManualConfirm 
                          ? 'If you have completed the payment, tap "I have paid" below.' 
                          : 'Complete payment in your UPI app. We\'ll automatically confirm once done.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {/* Manual Verify Button - Shows after 30 seconds */}
                  {showManualConfirm && (
                    <button
                      onClick={checkPaymentStatusManual}
                      disabled={manualChecking}
                      className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {manualChecking ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          I have paid - Verify Now
                        </>
                      )}
                    </button>
                  )}

                  {/* Refresh Status Button */}
                  <button
                    onClick={checkPaymentStatusManual}
                    disabled={manualChecking}
                    className="w-full py-3 bg-amber-100 hover:bg-amber-200 text-amber-800 font-medium rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${manualChecking ? 'animate-spin' : ''}`} />
                    Check Payment Status
                  </button>

                  {/* Cancel Button */}
                  <button
                    onClick={handleClose}
                    className="w-full py-3 text-gray-500 hover:text-gray-700 font-medium"
                  >
                    Cancel Payment
                  </button>
                </div>

                {/* Order ID */}
                <p className="text-xs text-gray-400 text-center mt-4">
                  Order ID: {order_id}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UPIPaymentModal;
